/**
 * Date and time parsing utilities for JHora import data
 * Extended to support coordinates extraction for API integration
 */

import { devError } from '../devLog';
import { ChartDateTime } from '../../models/storage';

// Interface for complete birth data extraction
export interface ExtractedBirthData {
  chartDateTime: ChartDateTime;
  latitude: number;
  longitude: number;
}

export interface BirthDataExtractionResult {
  success: boolean;
  data?: ExtractedBirthData;
  error?: string;
}

// Regex patterns for extracting date/time from JHora text
const DATE_PATTERNS = [
  /Date:\s*([A-Za-z]+\s+\d{1,2},\s+\d{4})/i,           // "Date: May 27, 2025"
  /Date:\s*(\d{1,2}[/.-]\d{1,2}[/.-]\d{4})/i,        // "Date: 27/05/2025" or "27-05-2025"
  /Date:\s*(\d{4}[/.-]\d{1,2}[/.-]\d{1,2})/i,        // "Date: 2025/05/27" or "2025-05-27"
  /(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i,                     // "27 May 2025" (without "Date:" prefix)
];

const TIME_PATTERNS = [
  /Time:\s*(\d{1,2}:\d{2}:\d{2})/i,                     // "Time: 23:09:13"
  /Time:\s*(\d{1,2}:\d{2})/i,                           // "Time: 23:09"
  /(\d{1,2}:\d{2}:\d{2})/,                              // "23:09:13" (without "Time:" prefix)
];

// Remove unused TIMEZONE_PATTERNS variable
// Timezone parsing is handled directly in parseTimezone function

const LOCATION_PATTERNS = [
  /Place:\s*[^,\n]*,\s*[^,\n]*\n\s*([^,\n]+(?:,\s*[^,\n]+)*)/i,  // Multi-line place info
  /([A-Za-z][A-Za-z\s]+,\s*[A-Za-z][A-Za-z\s]+)$/m,              // "City, Country" at end of line
  /Place:\s*([^,\n]+(?:,\s*[^,\n]+)*)/i,                          // "Place: City, Country"
  /Location:\s*([^,\n]+(?:,\s*[^,\n]+)*)/i,                       // "Location: City, Country"
];

/**
 * Converts month names to numbers
 */
const MONTH_MAP: Record<string, number> = {
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sep: 9, sept: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12
};

/**
 * Parses date string in various formats to YYYY-MM-DD
 */
function parseDate(dateStr: string): string | null {
  try {
    // Handle "May 27, 2025" format
    const monthDayYear = dateStr.match(/([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})/);
    if (monthDayYear) {
      const [, monthName, day, year] = monthDayYear;
      const month = MONTH_MAP[monthName.toLowerCase()];
      if (month) {
        return `${year}-${month.toString().padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    // Handle "27/05/2025" or "27-05-2025" format (DD/MM/YYYY)
    const ddmmyyyy = dateStr.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // Handle "2025/05/27" or "2025-05-27" format (YYYY/MM/DD)
    const yyyymmdd = dateStr.match(/(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})/);
    if (yyyymmdd) {
      const [, year, month, day] = yyyymmdd;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    // Handle "27 May 2025" format
    const dayMonthYear = dateStr.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
    if (dayMonthYear) {
      const [, day, monthName, year] = dayMonthYear;
      const month = MONTH_MAP[monthName.toLowerCase()];
      if (month) {
        return `${year}-${month.toString().padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Parses time string to HH:mm:ss format
 */
function parseTime(timeStr: string): string | null {
  try {
    // Handle "HH:mm:ss" format
    const hhmmss = timeStr.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    if (hhmmss) {
      const [, hours, minutes, seconds] = hhmmss;
      return `${hours.padStart(2, '0')}:${minutes}:${seconds}`;
    }

    // Handle "HH:mm" format (assume seconds = 00)
    const hhmm = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (hhmm) {
      const [, hours, minutes] = hhmm;
      return `${hours.padStart(2, '0')}:${minutes}:00`;
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Parses timezone information to standardized format
 */
function parseTimezone(text: string): string {
  try {
    // Look for "Time Zone: 3:00:00 (East of GMT)" pattern
    const eastWestMatch = text.match(/Time Zone:\s*(\d{1,2}):(\d{2}):\d{2}\s+\((East|West)\s+of\s+GMT\)/i);
    if (eastWestMatch) {
      const [, hours, minutes, direction] = eastWestMatch;
      const sign = direction.toLowerCase() === 'east' ? '+' : '-';
      return `${sign}${hours.padStart(2, '0')}:${minutes}`;
    }

    // Look for explicit timezone offset like "+3:00" or "-5:00"
    const offsetMatch = text.match(/Time Zone:\s*([+-]\d{1,2}:\d{2})/);
    if (offsetMatch) {
      let offset = offsetMatch[1];
      // Ensure proper format
      const parts = offset.match(/([+-])(\d{1,2}):(\d{2})/);
      if (parts) {
        const [, sign, hours, minutes] = parts;
        return `${sign}${hours.padStart(2, '0')}:${minutes}`;
      }
      return offset;
    }

    // Default to UTC if no timezone found
    return '+00:00';
  } catch (error) {
    return '+00:00';
  }
}

/**
 * Parses coordinate string like "34 E 33' 00"" or "48 N 35' 42""
 * @param coordStr - Coordinate string
 * @returns Decimal degrees (positive for N/E, negative for S/W)
 */
function parseCoordinate(coordStr: string): number {
  // Match format: "34 E 33' 00"" or "48 N 35' 42""
  const match = coordStr.match(/(\d+)\s+([NSEW])\s+(\d+)'\s+(\d+)"/);
  
  if (!match) {
    throw new Error(`Invalid coordinate format: ${coordStr}`);
  }
  
  const [, degrees, direction, minutes, seconds] = match;
  
  const decimal = parseInt(degrees, 10) + 
                 (parseInt(minutes, 10) / 60) + 
                 (parseInt(seconds, 10) / 3600);
  
  // Apply direction (S and W are negative)
  return ['S', 'W'].includes(direction) ? -decimal : decimal;
}

/**
 * Extracts coordinates from Place line
 * Format: "Place:         34 E 33' 00", 48 N 35' 42""
 */
function parseCoordinates(text: string): { latitude: number; longitude: number } | null {
  try {
    // Look for Place line with coordinates
    const placeMatch = text.match(/Place:\s*([^,\n]+),\s*([^,\n]+)/);
    if (!placeMatch) {
      return null;
    }
    
    const [, coord1, coord2] = placeMatch;
    
    // Parse both coordinates
    const firstCoord = parseCoordinate(coord1.trim());
    const secondCoord = parseCoordinate(coord2.trim());
    
    // Determine which is latitude and which is longitude
    // First coordinate should be longitude (E/W), second should be latitude (N/S)
    const longitude = coord1.includes('E') || coord1.includes('W') ? firstCoord : secondCoord;
    const latitude = coord1.includes('N') || coord1.includes('S') ? firstCoord : secondCoord;
    
    return { latitude, longitude };
  } catch (error) {
    return null;
  }
}

/**
 * Extracts location information from text
 */
function parseLocation(text: string): string | null {
  try {
    // Special handling for the JHora format where location appears after Place: line
    // Format:
    // Place:         34 E 33' 01", 48 N 35' 43"
    //                Kamenskoe, Ukraine
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('Place:')) {
        // Check the next line for location name
        if (i + 1 < lines.length) {
          const nextLine = lines[i + 1].trim();
          // Look for city, country pattern (letters, possibly with spaces, comma, more letters)
          const locationMatch = nextLine.match(/^([A-Za-z][A-Za-z\s]+,\s*[A-Za-z][A-Za-z\s]+)$/);
          if (locationMatch) {
            return locationMatch[1].trim();
          }
        }
      }
    }

    // Fallback to other patterns
    for (const pattern of LOCATION_PATTERNS) {
      const match = text.match(pattern);
      if (match) {
        const location = match[1].trim();
        // Skip if it looks like coordinates (contains numbers, degrees, etc.)
        if (!/\d+\s*[EW]\s*\d+|°|'|"/.test(location)) {
          return location;
        }
      }
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Main function to extract date/time information from JHora import data
 */
export function extractChartDateTime(importText: string): ChartDateTime | null {
  try {
    let date: string | null = null;
    let time: string | null = null;
    let originalDateStr: string | null = null;
    let originalTimeStr: string | null = null;

    // Extract date
    for (const pattern of DATE_PATTERNS) {
      const match = importText.match(pattern);
      if (match) {
        originalDateStr = match[1];
        date = parseDate(match[1]);
        if (date) break;
      }
    }

    // Extract time
    for (const pattern of TIME_PATTERNS) {
      const match = importText.match(pattern);
      if (match) {
        originalTimeStr = match[1];
        time = parseTime(match[1]);
        if (time) break;
      }
    }

    // If we couldn't extract both date and time, return null
    if (!date || !time) {
      return null;
    }

    // Extract timezone and location
    const timezone = parseTimezone(importText);
    const location = parseLocation(importText);

    return {
      date,
      time,
      timezone,
      location: location || undefined,
      originalDateStr: originalDateStr || undefined,
      originalTimeStr: originalTimeStr || undefined
    };

  } catch (error) {
    devError('Error extracting chart date/time:', error);
    return null;
  }
}

/**
 * Generates a default chart name based on extracted date/time
 */
export function generateDefaultChartName(chartDateTime: ChartDateTime | null): string {
  if (!chartDateTime) {
    // Fallback to current timestamp if no datetime extracted
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].substring(0, 5).replace(':', '-');
    return `[edit]${dateStr}-${timeStr}-from-imported-data`;
  }

  const { date, time } = chartDateTime;
  const timeFormatted = time.substring(0, 5).replace(':', '-'); // HH-mm format
  return `[edit]${date}-${timeFormatted}-from-imported-data`;
}

/**
 * Main function to extract complete birth data for API usage
 * Extracts date, time, timezone, coordinates, and location
 */
export function extractCompleteBirthData(natalChartText: string): BirthDataExtractionResult {
  try {
    // Extract date/time information
    const chartDateTime = extractChartDateTime(natalChartText);
    if (!chartDateTime) {
      return {
        success: false,
        error: 'Could not extract date/time information from the data'
      };
    }
    
    // Extract coordinates
    const coordinates = parseCoordinates(natalChartText);
    if (!coordinates) {
      return {
        success: false,
        error: 'Could not extract coordinates from the data'
      };
    }
    
    return {
      success: true,
      data: {
        chartDateTime,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      }
    };
    
  } catch (error) {
    return {
      success: false,
      error: `Failed to extract birth data: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Validates extracted date/time for reasonableness
 */
export function validateChartDateTime(chartDateTime: ChartDateTime): string[] {
  const errors: string[] = [];

  try {
    // Validate date format and range
    const dateObj = new Date(chartDateTime.date);
    if (isNaN(dateObj.getTime())) {
      errors.push('Invalid date format');
    } else {
      const year = dateObj.getFullYear();
      if (year < 1900 || year > 2100) {
        errors.push('Date year seems unreasonable (should be between 1900-2100)');
      }
    }

    // Validate time format
    const timeMatch = chartDateTime.time.match(/^(\d{2}):(\d{2}):(\d{2})$/);
    if (!timeMatch) {
      errors.push('Invalid time format (should be HH:mm:ss)');
    } else {
      const [, hours, minutes, seconds] = timeMatch;
      const h = parseInt(hours), m = parseInt(minutes), s = parseInt(seconds);
      if (h < 0 || h > 23) errors.push('Invalid hour (should be 0-23)');
      if (m < 0 || m > 59) errors.push('Invalid minute (should be 0-59)');
      if (s < 0 || s > 59) errors.push('Invalid second (should be 0-59)');
    }

    // Validate timezone format
    const tzMatch = chartDateTime.timezone.match(/^[+-]\d{2}:\d{2}$/);
    if (!tzMatch) {
      errors.push('Invalid timezone format (should be ±HH:mm)');
    }

  } catch (error) {
    errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return errors;
}
